import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { IManualRequestEditDialogResult } from '@app/shared/models/custom/manual-request-edit-dialog-result';
import { RequestViewModel } from '@features/teladoc-events/models/ui/request-view-model';
import { ArraySortPipe, AskForChecksService, ControlErrorDirective, DialogResultTypes, HowdenAlertService, IAskForChecksData, IDialogResult, IHowdenItem, LabelValueComponent } from '@howdeniberia/core-front';
import { RequestMapper } from '../../mappers/request-mapper';
import { ICompaniesCapitalsDto } from '../../models/ui/companies-capitals-dto';
import { EditManualRequestViewModel } from '../../models/ui/edit-manual-request-view-model';
import { CompanyRequest } from './../../../../shared/enums/common-enum';

@Component({
  selector: 'howden-manual-request-edit-dialog',
  templateUrl: './manual-request-edit-dialog.component.html',
  styleUrls: ['./manual-request-edit-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    CdkScrollable,
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatIcon,
    LabelValueComponent,
    ControlErrorDirective,
    ArraySortPipe
  ]
})
export class ManualRequestEditDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ManualRequestEditDialogComponent>>(MatDialogRef);
  protected data = inject<IManualRequestEditDialogResult>(MAT_DIALOG_DATA);
  private askForChecksSrv = inject(AskForChecksService);
  private warningSrv = inject(HowdenAlertService);

  model: EditManualRequestViewModel = new EditManualRequestViewModel();
  showAdvice = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _companiesInfoRaw: ICompaniesCapitalsDto;

  ngOnInit(): void {
    this.model.loadFromModel(this.data.value);
    this.loadCompaniesRaw(this.data.value);
    this.showAdvice = this.data.value.advice;
    if (this.showAdvice) {
      this.warningSrv.warning('Atención, pregunta única', 'Alguna de las compañías dependen de la pregunta única de la solicitud, revise las solicitudes', true);
    }
  }

  private loadCompaniesRaw(data: RequestViewModel): void {
    const valCompany = data.companies.split(';');
    const valCapitals = data.capitals.split(';');

    this._companiesInfoRaw = {
      capitals: [],
      companies: []
    } as ICompaniesCapitalsDto;

    for (const [key, value] of Object.entries(valCapitals)) {
      this._companiesInfoRaw.capitals.push(value);
      this._companiesInfoRaw.companies.push(valCompany[+key]);
    }
  }

  public get numbersOK(): boolean {
    const land = this.model.form.get(this.model.C_LANDLINE)?.value;
    const mob = this.model.form.get(this.model.C_MOBILE)?.value;
    const res = new RequestMapper().validateContactNumbers(land?.toString(), mob?.toString());
    return res;
  }

  public get capitalsKO(): boolean {
    const res = this.model.capitals.includes('X');
    return res;
  }

  /* #region Select Options */
  selectCompanies(): void {
    const options = {
      title: 'Seleccione Cía',
      subTitle: 'Pueden ser más de una',
      label: 'Compañías',
      allowCancel: true,
      items: this.getCompaniesOptions(),
      initialValue: this.getCompaniesSelected()
    } as IAskForChecksData;

    this.askForChecksSrv.open(options).subscribe((result: IDialogResult<IHowdenItem[]>) => {
      const currentVal = this.model.companies;
      if (result.result === DialogResultTypes.OK) {
        const val = result.resultData?.length === 0 ? '' : result.resultData?.map(x => x.description).join(';');
        this.model.companies = val ? val : currentVal;
        // update capitals
        this.model.capitals = this.reWriteCapitals();
        if (this.capitalsKO) {
          this.model.form.get(this.model.C_CAPITALS)?.setErrors({ 'wrongValue': true });
        }
      }
    });
  }

  private reWriteCapitals(): string {
    const companiesSelected = this.model.companies.split(';');
    const result: Array<string> = [];
    companiesSelected.forEach(company => {
      const index = this._companiesInfoRaw.companies.indexOf(company);
      result.push(index > -1 ? `${this._companiesInfoRaw.capitals[index]}` : 'XXX');
    });
    return result.join(';');
  }

  private getCompaniesOptions(): IHowdenItem[] {
    const result: IHowdenItem[] = [];
    try {
      for (const key in CompanyRequest) {
        if (!isNaN(Number(key))) {
          result.push({ key: Number(key), description: CompanyRequest[key] } as IHowdenItem);
        }
      }
    } catch (ex) {
      // Error ignored
    }
    return result;
  }

  private getCompaniesSelected(): number[] {
    const result: number[] = [];
    const companiesValue = this.model.companies;
    if (companiesValue === '') {
      return result;
    }
    try {
      for (const key in CompanyRequest) {
        if (isNaN(Number(key))) {
          const companyValue = CompanyRequest[key];
          if (companiesValue.includes(key)) {
            result.push(+companyValue);
          }
        }
      }
    } catch (ex) {
      // Error ignored
    }
    return result;
  }

    /* #endregion */

  clean(): void {
    this.model.form.reset();
  }

  close(result: boolean): void {
    if (result && this.model.form.invalid) {
      return;
    }
    this.data.ok = result;
    this.cleanResult();
    this.data.value = new RequestMapper().editManualRequestViewModelToRequestViewModel(this.model);
    this.dialogRef.close(this.data);
  }

  cleanResult(): void {
    if (this.model.nif !== null) {
      this.model.nif = this.model.nif.trim().split('-').join('').split(' ').join('').split('(').join('').split(')').join('');
    }
    if (this.model.landLine !== null) {
      this.model.landLine = this.model.landLine.trim().split('-').join('').split(' ').join('').split('(').join('').split(')').join('');
    }
    if (this.model.mobilePhone !== null) {
      this.model.mobilePhone = this.model.mobilePhone.trim().split('-').join('').split(' ').join('').split('(').join('').split(')').join('');
    }
  }
}
